<template>
  <transition name="el-fade-in">
    <div id="BloomSurveyDialog" class="bloom-survey-mask" v-if="innerVisible" @click.self.stop>
      <div class="bloom-survey-dialog" role="dialog" aria-modal="true">
        <div class="survey-header">
          <div class="header-top">
            <span class="survey-tag">{{ $t('flowSurvey1') }}</span>
            <button type="button" class="survey-close" @click="handleClose" aria-label="close">
              <i class="close-icon"></i>
            </button>
          </div>
          <h3 class="survey-title">{{ $t('flowSurvey2') }}</h3>
        </div>

        <div class="survey-body">
          <p class="survey-hint">{{ $t('flowSurvey3') }}</p>

          <div class="option-grid">
            <div
              v-for="item in channelOptions"
              :key="item.id"
              class="option-item"
              :class="{ active: selectedIds.includes(item.id) }"
              @click="toggleOption(item.id)">
              <span class="option-check" :class="{ checked: selectedIds.includes(item.id) }"></span>
              <span class="option-label">{{ $t(item.i18nKey) }}</span>
            </div>
          </div>

          <div
            class="option-other"
            :class="{ active: selectedIds.includes(OTHER_ID) }"
            @click="toggleOption(OTHER_ID)">
            <span class="option-check" :class="{ checked: selectedIds.includes(OTHER_ID) }"></span>
            <span class="option-label other-label">{{ $t('flowSurvey14') }}：</span>
            <input
              ref="otherInput"
              class="other-input"
              type="text"
              maxlength="50"
              :placeholder="$t('flowSurvey15')"
              v-model="otherText"
              @click.stop="onOtherFocus"
              @focus="onOtherFocus"
              @input="onOtherFocus" />
          </div>
        </div>

        <div class="survey-footer">
          <button type="button" class="btn-skip" @click="handleSkip">{{ $t('flowSurvey16') }}</button>
          <button
            type="button"
            class="btn-submit"
            :class="{ disabled: !canSubmit }"
            :disabled="!canSubmit"
            @click="handleSubmit">
            {{ $t('flowSurvey17') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
const OTHER_ID = 11
const CHANNEL_OPTIONS = [
  { id: 1, i18nKey: 'flowSurvey4' },
  { id: 2, i18nKey: 'flowSurvey5' },
  { id: 3, i18nKey: 'flowSurvey6' },
  { id: 4, i18nKey: 'flowSurvey7' },
  { id: 5, i18nKey: 'flowSurvey8' },
  { id: 6, i18nKey: 'flowSurvey9' },
  { id: 7, i18nKey: 'flowSurvey10' },
  { id: 8, i18nKey: 'flowSurvey11' },
  { id: 9, i18nKey: 'flowSurvey12' },
  { id: 10, i18nKey: 'flowSurvey13' }
]

export default {
  name: 'BloomSurveyDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      OTHER_ID,
      channelOptions: CHANNEL_OPTIONS,
      selectedIds: [],
      otherText: '',
      show: false,
      submitting: false,
      onSubmit: null,
      onSkip: null,
      onClose: null
    }
  },
  computed: {
    innerVisible () {
      return this.show || this.visible
    },
    canSubmit () {
      return this.selectedIds.length > 0 && !this.submitting
    }
  },
  watch: {
    innerVisible (val) {
      if (val) {
        this.lockScroll()
      } else {
        this.unlockScroll()
        this.resetForm()
      }
    }
  },
  mounted () {
    try {
      const { setBloomSurveyHost } = require('../../plugin/BloomSurveyDialog/host')
      setBloomSurveyHost && setBloomSurveyHost(this)
    } catch (e) {}
  },
  beforeDestroy () {
    this.unlockScroll()
    try {
      const { setBloomSurveyHost, getBloomSurveyHost } = require('../../plugin/BloomSurveyDialog/host')
      if (setBloomSurveyHost && getBloomSurveyHost && getBloomSurveyHost() === this) {
        setBloomSurveyHost(null)
      }
    } catch (e) {}
  },
  methods: {
    open (options = {}) {
      this.onSubmit = typeof options.onSubmit === 'function' ? options.onSubmit : null
      this.onSkip = typeof options.onSkip === 'function' ? options.onSkip : null
      this.onClose = typeof options.onClose === 'function' ? options.onClose : null
      this.resetForm()
      this.show = true
      try {
        const { clearBloomSurveyOpening } = require('../../plugin/BloomSurveyDialog/pending')
        clearBloomSurveyOpening && clearBloomSurveyOpening()
      } catch (e) {}
    },
    hide () {
      this.show = false
      this.$emit('update:visible', false)
    },
    toggleOption (id) {
      const idx = this.selectedIds.indexOf(id)
      if (idx > -1) {
        this.selectedIds.splice(idx, 1)
        if (id === OTHER_ID) this.otherText = ''
      } else {
        this.selectedIds.push(id)
        if (id === OTHER_ID) {
          this.$nextTick(() => {
            this.$refs.otherInput && this.$refs.otherInput.focus()
          })
        }
      }
    },
    onOtherFocus () {
      if (!this.selectedIds.includes(OTHER_ID)) {
        this.selectedIds.push(OTHER_ID)
      }
    },
    handleClose () {
      this.onClose && this.onClose()
      this.$emit('close')
      this.hide()
    },
    handleSkip () {
      this.onSkip && this.onSkip()
      this.$emit('skip')
      this.hide()
    },
    handleSubmit () {
      if (!this.canSubmit) return
      const hasOther = this.selectedIds.includes(OTHER_ID)
      if (hasOther && !this.otherText.trim()) {
        this.$message({
          message: this.$t('flowSurvey20'),
          type: 'warning',
          customClass: 'bloom-survey-toast',
          duration: 2000
        })
        this.$nextTick(() => {
          this.$refs.otherInput && this.$refs.otherInput.focus()
        })
        return
      }
      const payload = {
        optionCodes: this.selectedIds.slice().sort((a, b) => a - b).map(String),
        otherText: hasOther ? this.otherText.trim() : ''
      }
      this.submitting = true
      this.$api.post('USER_QUESTIONNAIRE_SUBMIT', payload, () => {
        this.submitting = false
        this.onSubmit && this.onSubmit(payload)
        this.$emit('submit', payload)
        this.$message({
          message: this.$t('flowSurvey19'),
          type: 'success',
          customClass: 'bloom-survey-toast',
          duration: 2000
        })
        this.hide()
      }, () => {
        this.submitting = false
      }, () => {
        this.submitting = false
      })
    },
    resetForm () {
      this.selectedIds = []
      this.otherText = ''
      this.submitting = false
    },
    lockScroll () {
      document.body.style.overflow = 'hidden'
    },
    unlockScroll () {
      document.body.style.overflow = ''
    }
  }
}
</script>

<style scoped lang="scss">
.bloom-survey-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.55);
  z-index: 99999;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  box-sizing: border-box;
}

.bloom-survey-dialog {
  width: 640px;
  max-width: 100%;
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(41, 107, 255, 0.2);
}

.survey-header {
  position: relative;
  padding: 20px 24px 28px;
  /* 贴合平台蓝主题 #296BFF */
  background: linear-gradient(120deg, #2DA9FF 0%, #296BFF 55%, #1C7BFF 100%);
  box-sizing: border-box;
}

.header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.survey-tag {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 12px;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  font-size: 12px;
  line-height: 26px;
  font-family: PingFangSC-Regular, PingFang SC, sans-serif;
}

.survey-close {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  outline: none;

  &:hover {
    background: rgba(255, 255, 255, 0.32);
  }
}

.close-icon {
  position: relative;
  width: 12px;
  height: 12px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 1.5px;
    background: #fff;
    border-radius: 1px;
  }

  &::before {
    transform: translateY(-50%) rotate(45deg);
  }

  &::after {
    transform: translateY(-50%) rotate(-45deg);
  }
}

.survey-title {
  margin: 0;
  color: #fff;
  font-size: 22px;
  line-height: 32px;
  font-weight: 600;
  font-family: PingFangSC-Semibold, PingFang SC, sans-serif;
}

.survey-body {
  padding: 20px 24px 8px;
  box-sizing: border-box;
}

.survey-hint {
  margin: 0 0 16px;
  color: #999;
  font-size: 13px;
  line-height: 20px;
  font-family: PingFangSC-Regular, PingFang SC, sans-serif;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.option-item,
.option-other {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 10px 12px;
  background: #F5F6F8;
  border-radius: 10px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  border: 1px solid transparent;

  &.active {
    background: #EEF4FF;
    border-color: rgba(41, 107, 255, 0.35);
  }
}

.option-check {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border: 1.5px solid #C5C5C5;
  border-radius: 4px;
  box-sizing: border-box;
  position: relative;
  background: #fff;

  &.checked {
    border-color: #296BFF;
    background: #296BFF;

    &::after {
      content: '';
      position: absolute;
      left: 4.5px;
      top: 2px;
      width: 4px;
      height: 7px;
      border: 1.5px solid #fff;
      border-top: none;
      border-left: none;
      transform: rotate(45deg);
    }
  }
}

.option-label {
  color: #222;
  font-size: 13px;
  line-height: 18px;
  font-family: PingFangSC-Regular, PingFang SC, sans-serif;
  word-break: break-word;
}

.option-other {
  margin-top: 12px;
  min-height: 48px;
  width: 100%;
}

.other-label {
  flex-shrink: 0;
  white-space: nowrap;
}

.other-input {
  flex: 1;
  min-width: 120px;
  margin-left: 8px;
  border: none;
  border-bottom: 1px solid #C8C8C8;
  outline: none;
  background: transparent;
  height: 28px;
  font-size: 13px;
  color: #222;
  font-family: PingFangSC-Regular, PingFang SC, sans-serif;

  &::placeholder {
    color: #BBBBBB;
  }

  &:focus {
    border-bottom-color: #296BFF;
  }
}

.survey-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 16px 24px 24px;
  box-sizing: border-box;
}

.btn-skip,
.btn-submit {
  min-width: 88px;
  height: 36px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  font-family: PingFangSC-Regular, PingFang SC, sans-serif;
  outline: none;
}

.btn-skip {
  background: #F0F0F0;
  color: #555;

  &:hover {
    background: #E8E8E8;
  }
}

.btn-submit {
  background: #296BFF;
  color: #fff;

  &:hover:not(.disabled) {
    background: #1C5AE6;
  }

  &.disabled,
  &:disabled {
    background: #A8C4FF;
    color: #fff;
    cursor: not-allowed;
  }
}
</style>

<style lang="scss">
/* Element Message 挂到 body，需非 scoped；高于调研弹窗遮罩 99999 */
.el-message.bloom-survey-toast {
  z-index: 100000 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/public/BloomSurveyDialog.vue